import { Extension } from "@tiptap/core"
import { Plugin, PluginKey } from "@tiptap/pm/state"
import { Decoration, DecorationSet } from "@tiptap/pm/view"
import { DOMSerializer } from "@tiptap/pm/model"

const paragraphSelectorKey = new PluginKey("paragraphSelector")

const EDITABLE_TYPES = ["paragraph", "heading", "blockquote"]

function getHTMLFromRange(editor, from, to) {
  const slice = editor.state.doc.slice(from, to)
  const serializer = DOMSerializer.fromSchema(editor.state.schema)
  const fragment = serializer.serializeFragment(slice.content)
  const div = document.createElement("div")
  div.appendChild(fragment)
  return div.innerHTML
}

export const ParagraphSelector = Extension.create({
  name: "paragraphSelector",

  addOptions() {
    return {
      onParagraphSelect: null,
    }
  },

  addStorage() {
    return {
      selectedFrom: null,
      selectedTo: null,
      active: false,
    }
  },

  addCommands() {
    return {
      selectParagraph:
        (from, to) =>
        ({ tr, dispatch }) => {
          if (dispatch) {
            tr.setMeta(paragraphSelectorKey, {
              type: "selectParagraph",
              from,
              to,
            })
            dispatch(tr)
          }
          return true
        },
      clearParagraphSelection:
        () =>
        ({ tr, dispatch }) => {
          if (dispatch) {
            tr.setMeta(paragraphSelectorKey, { type: "clearSelection" })
            dispatch(tr)
          }
          return true
        },
      setParagraphSelectorActive:
        (active) =>
        ({ editor }) => {
          editor.storage.paragraphSelector.active = active
          return true
        },
    }
  },

  addProseMirrorPlugins() {
    const extension = this

    return [
      new Plugin({
        key: paragraphSelectorKey,

        state: {
          init() {
            return DecorationSet.empty
          },
          apply(tr, oldDecoSet) {
            let decoSet = oldDecoSet.map(tr.mapping, tr.doc)
            const meta = tr.getMeta(paragraphSelectorKey)

            if (meta?.type === "selectParagraph") {
              const decoration = Decoration.node(meta.from, meta.to, {
                class: "paragraph-selected",
              })
              decoSet = DecorationSet.create(tr.doc, [decoration])

              // Update storage for external access
              extension.storage.selectedFrom = meta.from
              extension.storage.selectedTo = meta.to
            } else if (meta?.type === "clearSelection") {
              decoSet = DecorationSet.empty
              extension.storage.selectedFrom = null
              extension.storage.selectedTo = null
            }

            return decoSet
          },
        },

        props: {
          decorations(state) {
            return this.getState(state)
          },

          handleClick(view, pos, event) {
            if (!extension.storage.active) return false
            const { onParagraphSelect } = extension.options
            if (!onParagraphSelect) return false

            const $pos = view.state.doc.resolve(pos)
            let depth = $pos.depth

            while (depth > 0) {
              const node = $pos.node(depth)
              if (
                EDITABLE_TYPES.includes(node.type.name) &&
                node.textContent.trim()
              ) {
                const from = $pos.before(depth)
                const to = from + node.nodeSize

                // Dispatch the decoration
                const tr = view.state.tr
                tr.setMeta(paragraphSelectorKey, {
                  type: "selectParagraph",
                  from,
                  to,
                })
                view.dispatch(tr)

                // Get HTML of the node
                const html = getHTMLFromRange(
                  { state: view.state },
                  from,
                  to
                )

                onParagraphSelect({
                  text: node.textContent,
                  html,
                  from,
                  to,
                  nodeType: node.type.name,
                  contentHash: node.textContent.slice(0, 50),
                })

                return false // Don't prevent default cursor behavior
              }
              depth--
            }

            return false
          },
        },
      }),
    ]
  },
})
